<script setup>
// NOTICE: KAPTION-INTEGRATION
// whatsapp-call-transcripts.REC.UI / FLAGS.1 / RET.1 — the inbox Calling
// tab's "Recording" section (admins, KAPTION_CALL_RECORDING): record calls,
// transcribe them (KAPTION_CALL_TRANSCRIPTION), how customers are told
// (owner decision 2026-10-09: played automatically when the call starts /
// a button agents press / agents say it themselves), the notice language
// with a preview, and how long recordings and transcripts are kept. Saved
// on its own (PATCH, no Meta call); the decision is copied onto each call
// when it starts, so a change never affects a live call.
import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import {
  DEFAULT_RECORDING,
  NOTICE_SCRIPTS,
  RECORDING_NOTICE_MODES,
} from 'dashboard/api/kaptionCalls';
import { useKaptionCallStore } from 'dashboard/components-next/kaption/calls/kaptionCallStore';

const props = defineProps({
  inbox: { type: Object, required: true },
  // fromBackendRecording(settings.recording)
  initial: { type: Object, default: () => ({ ...DEFAULT_RECORDING }) },
  blocked: { type: Boolean, default: false },
});

const { t } = useI18n();
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
const calls = useKaptionCallStore();

const LANGUAGES = [
  { value: 'es', key: 'ES', bcp47: 'es-AR' },
  { value: 'pt', key: 'PT', bcp47: 'pt-BR' },
  { value: 'en', key: 'EN', bcp47: 'en-US' },
];
const RETENTION_DAYS = [30, 60, 90, 180, 365];
const MODE_COPY = {
  auto: 'AUTO',
  button: 'BUTTON',
  agent: 'AGENT',
};

const form = reactive({ ...DEFAULT_RECORDING });
const hydrate = value => Object.assign(form, DEFAULT_RECORDING, value || {});
watch(() => props.initial, hydrate, { immediate: true, deep: true });

const showTranscribe = computed(() =>
  isKaptionFlagEnabled('KAPTION_CALL_TRANSCRIPTION')
);
const script = computed(
  () => NOTICE_SCRIPTS[form.notice_language] || NOTICE_SCRIPTS.es
);
const quotedScript = computed(() => `“${script.value}”`);

// ---- preview: the real prompt when the backend serves one, else the
// browser's voice reading the script (labelled the same: it is a preview).
const previewing = ref(false);
let previewAudio = null;
const stopPreview = () => {
  previewing.value = false;
  if (previewAudio) {
    previewAudio.pause();
    previewAudio = null;
  }
  try {
    window.speechSynthesis?.cancel();
  } catch (e) {
    // no speech synthesis
  }
};
const playPreview = () => {
  if (previewing.value) {
    stopPreview();
    return;
  }
  previewing.value = true;
  const url = form.notice_preview_url;
  if (url) {
    previewAudio = new Audio(url);
    previewAudio.onended = stopPreview;
    previewAudio.play().catch(stopPreview);
    return;
  }
  try {
    const utterance = new SpeechSynthesisUtterance(script.value);
    utterance.lang =
      LANGUAGES.find(l => l.value === form.notice_language)?.bcp47 || 'es-AR';
    utterance.onend = stopPreview;
    window.speechSynthesis.speak(utterance);
  } catch (e) {
    stopPreview();
  }
};
onBeforeUnmount(stopPreview);

const isSaving = ref(false);
const save = async () => {
  isSaving.value = true;
  try {
    const { data } = await calls
      .api()
      .updateInboxRecording(props.inbox.id, { ...form });
    if (data?.recording) hydrate(data.recording);
    useAlert(t('KAPTION_CALLS.RECORDING_SETTINGS.SAVED'));
  } catch (e) {
    useAlert(t('KAPTION_CALLS.RECORDING_SETTINGS.SAVE_FAILED'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <section
    data-testid="kaption-calling-recording"
    class="flex flex-col gap-5 rounded-lg border border-n-weak bg-n-background p-5"
    :class="{ 'pointer-events-none opacity-60': blocked }"
  >
    <div>
      <h4
        class="mb-1.5 flex items-center gap-1.5 text-[11px] font-medium uppercase leading-none tracking-[0.08em] text-n-slate-10"
      >
        <span class="i-lucide-mic size-3.5 text-[#DC2626]" />
        {{ $t('KAPTION_CALLS.RECORDING_SETTINGS.TITLE') }}
      </h4>
      <p class="mb-0 text-xs text-n-slate-11">
        {{ $t('KAPTION_CALLS.RECORDING_SETTINGS.HINT') }}
      </p>
    </div>

    <label class="flex items-center justify-between gap-4">
      <span class="flex flex-col">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('KAPTION_CALLS.RECORDING_SETTINGS.ENABLE') }}
        </span>
        <span class="text-xs text-n-slate-11">
          {{ $t('KAPTION_CALLS.RECORDING_SETTINGS.ENABLE_HINT') }}
        </span>
      </span>
      <Switch v-model="form.enabled" data-testid="kaption-recording-enabled" />
    </label>

    <template v-if="form.enabled">
      <label
        v-if="showTranscribe"
        class="flex items-center justify-between gap-4"
      >
        <span class="flex flex-col">
          <span
            class="flex items-center gap-1.5 text-sm font-medium text-n-slate-12"
          >
            <span class="i-lucide-audio-lines size-3.5 text-[#F97316]" />
            {{ $t('KAPTION_CALLS.RECORDING_SETTINGS.TRANSCRIBE') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ $t('KAPTION_CALLS.RECORDING_SETTINGS.TRANSCRIBE_HINT') }}
          </span>
        </span>
        <Switch
          v-model="form.transcribe"
          data-testid="kaption-recording-transcribe"
        />
      </label>

      <div class="flex flex-col gap-2">
        <span
          class="text-[11px] font-medium uppercase leading-none tracking-[0.08em] text-n-slate-10"
        >
          {{ $t('KAPTION_CALLS.RECORDING_SETTINGS.NOTICE') }}
        </span>
        <label
          v-for="mode in RECORDING_NOTICE_MODES"
          :key="mode"
          :data-testid="`kaption-recording-notice-${mode}`"
          class="flex cursor-pointer items-start gap-3 rounded-lg border p-3"
          :class="
            form.notice_mode === mode
              ? 'border-n-brand bg-n-brand/5'
              : 'border-n-weak bg-n-solid-1 hover:bg-n-alpha-1'
          "
        >
          <input
            v-model="form.notice_mode"
            type="radio"
            name="kaption-recording-notice"
            class="!mb-0 mt-0.5"
            :value="mode"
          />
          <span class="flex flex-col">
            <span class="text-sm font-medium text-n-slate-12">
              {{
                $t(
                  `KAPTION_CALLS.RECORDING_SETTINGS.NOTICE_${MODE_COPY[mode]}_TITLE`
                )
              }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{
                $t(
                  `KAPTION_CALLS.RECORDING_SETTINGS.NOTICE_${MODE_COPY[mode]}_BODY`
                )
              }}
            </span>
          </span>
        </label>
      </div>

      <div
        v-if="form.notice_mode !== 'agent'"
        data-testid="kaption-recording-notice-preview"
        class="flex flex-col gap-2.5 rounded-lg border border-n-weak bg-n-solid-1 p-3"
      >
        <div class="flex items-center justify-between gap-3">
          <label class="flex items-center gap-2 text-sm">
            <span class="text-xs text-n-slate-11">
              {{ $t('KAPTION_CALLS.RECORDING_SETTINGS.LANGUAGE') }}
            </span>
            <select
              v-model="form.notice_language"
              data-testid="kaption-recording-language"
              class="!mb-0 h-8 w-36 rounded-md border border-n-weak bg-n-alpha-black2 !py-0 px-2 text-sm text-n-slate-12"
            >
              <option
                v-for="lang in LANGUAGES"
                :key="lang.value"
                :value="lang.value"
              >
                {{ $t(`KAPTION_CALLS.TRANSCRIPT.LANGUAGES.${lang.key}`) }}
              </option>
            </select>
          </label>
          <Button
            data-testid="kaption-recording-preview"
            :icon="previewing ? 'i-lucide-square' : 'i-lucide-play'"
            :label="
              previewing
                ? $t('KAPTION_CALLS.RECORDING_SETTINGS.STOP_PREVIEW')
                : $t('KAPTION_CALLS.RECORDING_SETTINGS.PREVIEW')
            "
            color="slate"
            variant="faded"
            size="sm"
            @click="playPreview"
          />
        </div>
        <div class="flex flex-col gap-1">
          <span class="text-[11px] text-n-slate-10">
            {{ $t('KAPTION_CALLS.RECORDING_SETTINGS.NOTICE_TEXT') }}
          </span>
          <p
            data-testid="kaption-recording-script"
            class="mb-0 border-l-2 border-[#DC2626]/40 pl-2.5 text-sm italic leading-relaxed text-n-slate-12"
          >
            {{ quotedScript }}
          </p>
        </div>
      </div>

      <label class="flex flex-col gap-1 text-sm">
        <span class="text-xs text-n-slate-11">
          {{ $t('KAPTION_CALLS.RECORDING_SETTINGS.RETENTION') }}
        </span>
        <select
          v-model.number="form.retention_days"
          data-testid="kaption-recording-retention"
          class="!mb-0 h-9 w-40 rounded-md border border-n-weak bg-n-alpha-black2 !py-0 px-2 text-sm text-n-slate-12"
        >
          <option v-for="n in RETENTION_DAYS" :key="n" :value="n">
            {{ $t('KAPTION_CALLS.RECORDING_SETTINGS.RETENTION_DAYS', { n }) }}
          </option>
        </select>
        <span class="text-xs text-n-slate-11">
          {{ $t('KAPTION_CALLS.RECORDING_SETTINGS.RETENTION_HINT') }}
        </span>
      </label>

      <p
        class="mb-0 flex items-start gap-2 rounded-md bg-n-alpha-1 px-3 py-2 text-xs text-n-slate-11"
      >
        <span class="i-lucide-scale mt-px size-3.5 shrink-0" />
        {{ $t('KAPTION_CALLS.RECORDING_SETTINGS.LEGAL') }}
      </p>
    </template>

    <div class="flex justify-end">
      <Button
        data-testid="kaption-recording-save"
        :label="$t('KAPTION_CALLS.RECORDING_SETTINGS.SAVE')"
        :is-loading="isSaving"
        :disabled="blocked"
        @click="save"
      />
    </div>
  </section>
</template>
